import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useEffect } from 'react';
import { toast } from 'sonner';
import { FormFieldType } from '../../../../extViewmodels';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { postNotification } from '../../../../models/LocalNotifications';
import { Topics } from '../../../../models/LocalNotificationTopics';
import { Button } from '../../../../web/components-v2/Button';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { useDealModal } from '../../../contexts/dealModal';
import { useQueueLeadNote } from '../../../contexts/queueLeadNote';
import { useTelephony } from '../../../contexts/telephony';
import { useErrorMessages, useGamification } from '../../../hooks';
import { useQueue } from '../../../hooks/queue';
import { aidaBaseStyleSheet } from '../../../styles/styles';
import { AidaNoteViewModel } from '../../../viewModels/note';
import { ISkipLeadRequest } from '../../../viewModels/queue';
import { AnimatedLionGraphic } from '../../svgs/AnimatedLionGraphic';
import { HexGraphic } from '../../svgs/HexGraphic';
import { DealField } from './DealField';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
	onClose?: () => void;
}

const DealDrawerContentBase: React.FC<IProps> = ({ className = '', onClose }) => {
	const queue = useQueue();
	const { phoneCalls } = useTelephony();
	const gamification = useGamification();
	const userSession = useUserSession();
	const dealModal = useDealModal();
	const errorMessages = useErrorMessages();
	const queueNote = useQueueLeadNote();

	useEffect(() => {
		if (queue.lead?.dealForm && !queue.lead?.dealForm?.form) {
			queue.lead.dealForm
				.loadForm()
				.then(() => {
					const fields = queue.lead.dealForm.fields.filter(x => x.fieldType === FormFieldType.Note);
					fields.forEach(field => {
						if (field.name === 'ExecutiveSummaryNote' && dealModal.note) {
							field.note = new AidaNoteViewModel(userSession, dealModal.note);
						} else if (field.name === 'OutcomeNotes' && dealModal.interactionNote) {
							field.note = new AidaNoteViewModel(userSession, dealModal.interactionNote);
						}
					});
				})
				.catch(err => {
					errorMessages.pushApiError(err);
				});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [queue.lead?.dealForm]);

	useEffect(() => {
		dealModal.setNote(null);
		dealModal.setInteractionNote(null);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [queue.companyId]);

	const handleClose = () => {
		queueNote.setForceClose(false);
		onClose?.();
	};

	const onActionClick = () => {
		const validationErrors = queue.lead?.dealForm?.form?.fields.filter(field => {
			field.validate();
			if (field.validationError) {
				document.getElementById(field.id + field.label + field.fieldType)?.scrollIntoView({ behavior: 'smooth' });
			}
			return !!field.validationError;
		});

		if (validationErrors?.length === 0) {
			const customFormData: ISkipLeadRequest = {
				leadServedSource: queue.leadServedSource,
			};

			const phoneCallId = phoneCalls.currentCall?.id;
			if (phoneCallId && phoneCallId !== 'manual') {
				customFormData.phoneCallId = phoneCallId;
			}

			queue.lead?.dealForm
				.save(customFormData)
				.then(meme => {
					if (meme) {
						gamification.setMeme(meme);
					}
					gamification.getScores().catch(err => {
						errorMessages.pushApiError(err);
					});

					toast.success(meme ? 'Deal created successfully' : 'Deal updated successfully');

					postNotification({
						topic: Topics.TIMELINE_REFRESH,
					});

					handleClose();
					phoneCalls.clearCurrentCall();

					queue.reloadLead().catch(err => errorMessages.pushApiError(err));
				})
				.catch(err => {
					errorMessages.pushApiError(err);
				});
		}
	};

	const renderDealForm = () => {
		if (queue.lead?.dealForm?.loadingForm) {
			return <LoadingSpinner type='large' />;
		}

		const fields = queue.lead?.dealForm?.form?.fields.map(field => (
			<DealField className={css(styleSheet.dealField)} field={field} form={queue.lead.dealForm} key={field.id} />
		));

		return (
			<div className={css(styleSheet.dealForm)}>
				{fields}
				{queue.lead?.dealForm?.saving && <LoadingSpinner className={css(aidaBaseStyleSheet.absoluteCenter)} />}
			</div>
		);
	};

	const label = queue.lead?.dealForm?.dealId
		? queue.lead?.dealForm?.saving
			? 'Updating...'
			: 'Update'
		: queue.lead?.dealForm?.saving
			? 'Creating...'
			: 'Create';

	return (
		<div className={`${css(styleSheet.dealDrawerContent)} ${className}`}>
			<div className={css(styleSheet.header)}>
				<div className={css(styleSheet.hexContainer)}>
					<HexGraphic animate={false} selected={true} className={css(styleSheet.hex)} />
					<AnimatedLionGraphic animate={false} className={css(styleSheet.lion)} />
				</div>

				<span className={css(styleSheet.headerTitle)}>
					{queue.lead?.dealForm?.dealId ? 'Update Deal' : 'Create Deal'}
				</span>
			</div>
			{renderDealForm()}
			{!(queue.lead?.dealForm?.loadingForm || queue.lead?.dealForm?.fields?.some(x => x?.isBusy)) && (
				<div className={css(styleSheet.ctaContainer)}>
					<Button disabled={queue.lead?.dealForm?.saving} onClick={onActionClick}>
						{label}
					</Button>
					<Button variant='secondary' disabled={queue.lead?.dealForm?.saving} onClick={handleClose}>
						Cancel
					</Button>
				</div>
			)}
		</div>
	);
};

export const DealDrawerContent = observer(DealDrawerContentBase);
